<template>
    <b-modal id="schema-input-copy-picker-modal"
             :title="$t('다른 스키마에서 복사')"
             size="xl"
             hide-footer
             modal-class="default-modal"
             @show="onShow"
    >
        <p class="text-secondary small mb-3">
            다른 프로젝트에 등록된 스키마 입력 값을 골라 <strong>복사</strong>하면, 현재 질문 추가 폼에
            draft(초안)로 채워집니다. 저장은 직접 눌러야 반영됩니다.
        </p>

        <div class="d-flex align-items-center mb-3">
            <b-form-input v-model="search"
                          :placeholder="$t('키 이름 또는 프로젝트로 검색')"
                          debounce="200"
                          style="max-width: 360px"
            />
            <b-button variant="outline-secondary" size="sm" class="ml-2" :disabled="loading" @click="fetchData">
                {{ $t('새로고침') }}
            </b-button>
            <span class="ml-auto text-secondary small">{{ filteredItems.length }} / {{ items.length }}</span>
        </div>

        <div class="table-container">
            <!-- DS 표 규칙: 행 44 고정 · 칸마다 한 줄(넘치면 줄이고 전체는 title).
                 고르기는 행 끝 '복사' 버튼으로만(행 클릭 선택 없음 — 아래 style 주석) -->
            <b-table responsive
                     :items="filteredItems"
                     :fields="fields"
                     class="mb-0"
                     table-class="ds-table ds-table-fixed schema-input-copy-table"
                     show-empty
                     :busy="loading"
            >
                <template #empty>
                    <page-empty-component
                        compact
                        :text="$t('조회된 스키마 입력 값가 없습니다.')"
                        :loading="loading"
                        :loading-text="$t('스키마 입력 값 목록을 조회 중입니다.')"
                    />
                </template>

                <template #cell(key_name)="row">
                    <span class="ds-ellipsis" :title="row.value">{{ row.value }}</span>
                </template>
                <template #cell(project_id)="row">
                    <span class="ds-ellipsis" :title="projectText(row.value)">{{ projectText(row.value) }}</span>
                </template>
                <template #cell(value_type)="row">
                    <span class="ds-tag">{{ valueTypeText(row.value) }}</span>
                </template>
                <template #cell(is_required)="row">
                    <span :class="row.value ? 'ds-status ds-status-warning' : 'ds-sub'">{{ row.value ? $t('필수') : $t('선택') }}</span>
                </template>
                <template #cell(action)="data">
                    <b-button size="sm" variant="outline-primary" @click="select(data.item)">
                        {{ $t('복사') }}
                    </b-button>
                </template>
            </b-table>
        </div>
    </b-modal>
</template>

<script>
import {listInputKeys} from '@shared_utils/api/projectSchema';
import {getTargetProjectText} from '@shared/components/admin/pages/Management/common-data';
import {valueTypeOptions} from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';

export default {
    name: 'SchemaInputCopyPickerModal',
    components: {PageEmptyComponent},
    props: {
        // 프로젝트 표시명 변환용 (getTargetProjectText).
        projects: {
            type: [Array, null],
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            items: [],
            search: '',
            // 폭을 안 적은 키 이름 · 프로젝트가 남는 폭을 나눠 갖는다(표 최소 폭은 아래 style).
            // 타입 110 은 '선택 목록' 태그가 들어가는 폭(목록 화면 ManageSchemaInput 과 같게)
            fields: [
                {key: 'key_name', label: '키 이름'},
                {key: 'project_id', label: '프로젝트'},
                {key: 'value_type', label: '타입', thStyle: {width: '110px'}},
                {key: 'is_required', label: '필수', thStyle: {width: '70px'}},
                {key: 'action', label: '복사', thStyle: {width: '90px'}, thClass: ['text-right'], tdClass: ['text-right']},
            ],
        };
    },
    computed: {
        filteredItems() {
            const q = (this.search || '').trim().toLowerCase();
            if (!q) return this.items;
            return this.items.filter((it) => {
                const name = (it.key_name || '').toLowerCase();
                const proj = this.projectText(it.project_id).toLowerCase();
                return name.includes(q) || proj.includes(q);
            });
        },
    },
    methods: {
        projectText(projectId) {
            return getTargetProjectText(projectId, this.projects) || String(projectId ?? '');
        },
        valueTypeText(valueType) {
            return valueTypeOptions.find((o) => o.value === valueType)?.text ?? '-';
        },
        onShow() {
            this.search = '';
            this.fetchData();
        },
        async fetchData() {
            try {
                this.loading = true;
                // project_id 없이 전체 프로젝트의 입력 값을 조회한다(백엔드가 optional 필터).
                const res = await listInputKeys({params: {}});
                const {input_keys} = res.data || {};
                this.items = Array.isArray(input_keys) ? input_keys : [];
            } catch (e) {
                console.error(e);
                this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 복사'), this.$t('스키마 입력 값 목록을 불러오는 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                this.items = [];
            } finally {
                this.loading = false;
            }
        },
        select(item) {
            // 부모(콘솔)가 질문 모달의 openAsCopy(id) 를 호출한다. 저장은 유저가 직접.
            this.$emit('select', item.id);
            this.$bvModal.hide('schema-input-copy-picker-modal');
        },
    },
};
</script>

<style scoped lang="scss">
// 고정 칸 합 270(타입 110 · 필수 70 · 복사 90) + 키 이름 · 프로젝트 225씩.
// xl 창 본문(1140 - 테두리 2 - default-modal 여백 48 = 약 1090) 안이고, 화면 1200 미만에서 800 창이 될 때(약 750)도 가로 스크롤 없이 들어간다
// 고르기는 행 끝 「복사」 버튼으로만 — 행 클릭으로 고르면 잘못 누른 행이 바로 질문 창에 채워진다
::v-deep .schema-input-copy-table {
    min-width: 720px;
}
</style>
